/**
 * Country Config Store
 * Manages country selection for map filtering with localStorage persistence.
 * 
 * Design:
 * - Admin/Analyst: Default country from server config, can select multiple
 * - Customer/Viewer: Auto-select from their available countries
 * - Selection persists across sessions via localStorage
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CountryInfo {
    code: string
    name: string
    count: number
}

interface CountryConfigState {
    // State
    selectedCountries: string[]
    availableCountries: CountryInfo[]
    defaultCountry: string
    isLoaded: boolean

    // Actions
    setSelectedCountries: (codes: string[]) => void
    toggleCountry: (code: string) => void
    selectAll: () => void
    clearSelection: () => void
    loadAvailableCountries: (countries: CountryInfo[], defaultCode: string) => void
    initializeSelection: (role: string) => void
    reset: () => void
}

// Limit for cache performance (as per plan.md risk assessment)
const MAX_SELECTED = 5

export const useCountryConfigStore = create<CountryConfigState>()(
    persist(
        (set, get) => ({
            // Initial state
            selectedCountries: [],
            availableCountries: [],
            defaultCountry: 'VN',
            isLoaded: false,

            setSelectedCountries: (codes: string[]) => {
                // Limit selection for performance
                const limited = codes.slice(0, MAX_SELECTED)
                set({ selectedCountries: limited })
            },

            toggleCountry: (code: string) => {
                const { selectedCountries } = get()
                if (selectedCountries.includes(code)) {
                    // Remove
                    set({ selectedCountries: selectedCountries.filter(c => c !== code) })
                } else {
                    // Add (respect limit)
                    if (selectedCountries.length < MAX_SELECTED) {
                        set({ selectedCountries: [...selectedCountries, code] })
                    }
                }
            },

            selectAll: () => {
                const { availableCountries } = get()
                const allCodes = availableCountries.slice(0, MAX_SELECTED).map(c => c.code)
                set({ selectedCountries: allCodes })
            },

            clearSelection: () => {
                set({ selectedCountries: [] })
            },

            loadAvailableCountries: (countries: CountryInfo[], defaultCode: string) => {
                set({
                    availableCountries: countries,
                    defaultCountry: defaultCode,
                    isLoaded: true
                })
            },

            initializeSelection: (role: string) => {
                const { availableCountries, defaultCountry, selectedCountries } = get()

                // Skip if already has selection (restored from localStorage)
                if (selectedCountries.length > 0) return

                // ALWAYS initialize with at least one country (never empty on first load)
                if (role === 'admin' || role === 'analyst') {
                    // Admin/Analyst: Use server default
                    const hasDefault = availableCountries.some(c => c.code === defaultCountry)
                    if (hasDefault) {
                        set({ selectedCountries: [defaultCountry] })
                    } else if (availableCountries.length > 0) {
                        set({ selectedCountries: [availableCountries[0].code] })
                    }
                    // Note: If no countries available, selection stays empty = empty map
                } else {
                    // Customer/Viewer: Auto-select first available
                    if (availableCountries.length > 0) {
                        set({ selectedCountries: [availableCountries[0].code] })
                    }
                    // Note: If customer has no assets in any country, selection stays empty
                }
            },

            reset: () => {
                set({
                    selectedCountries: [],
                    availableCountries: [],
                    defaultCountry: 'VN',
                    isLoaded: false
                })
            }
        }),
        {
            name: 'kcs-country-config',
            // Only persist selectedCountries to localStorage
            partialize: (state) => ({
                selectedCountries: state.selectedCountries
            })
        }
    )
)

// =============================================================================
// Selector Helpers
// =============================================================================

/** Get currently selected country codes */
export const useSelectedCountries = () =>
    useCountryConfigStore((s) => s.selectedCountries)

/** Get available countries list */
export const useAvailableCountries = () =>
    useCountryConfigStore((s) => s.availableCountries)

/**
 * Get countries as comma-separated string for API calls
 * Returns undefined if no countries selected (will cause empty map)
 */
export const useCountryFilterString = () =>
    useCountryConfigStore((s) =>
        s.selectedCountries.length > 0 ? s.selectedCountries.join(',') : undefined
    )
